import { AsyncPipe, formatDate, NgClass } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasConfirmationService,
  PasDateTimeComponent,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasSidebarHandleService,
  PasSpinnerComponent,
  PasTextareaComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { FirstDataRenderedEvent, GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { cloneDeep, isEqual } from 'lodash-es';
import moment from 'moment';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { SupplierService } from '../../../api/tur-rc/services/supplier.service';
import { checkUserRight } from '../../../services/after-sales/after-sales-functions';
import { SupplierExceptionsService } from '../../../services/after-sales/supplier-exceptions.service';
import { AGGridExcelService } from '../../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { AfterSalesTabsetComponent } from '../after-sales-tabset.component';
import { SupplierExceptionModel } from '../model/supplier-exceptions-model';
import { SupplierExceptionsButtonCellRenderer } from '../renderer/supplier-exceptions-button-cell-renderer.component';

const MIN_SUPPLIER_LENGTH = 5;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'supplier-exceptions',
  styleUrls: ['./supplier-exceptions.component.scss'],
  templateUrl: './supplier-exceptions.component.html',
  imports: [
    PasSpinnerComponent,
    PasTextareaComponent,
    TranslatePipe,
    AgGridAngular,
    PasLayoutSidebarHolderComponent,
    NgClass,
    GenericModalContentComponent,
    ReactiveFormsModule,
    PasInputNumberComponent,
    AsyncPipe,
    PasInputFieldComponent,
    PasDateTimeComponent,
  ],
})
export class SupplierExceptionsComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly supplierService = inject(SupplierService);
  private readonly supplierExceptionsService = inject(SupplierExceptionsService);
  private readonly modalService = inject(NgbModal);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly afterSalesTabset = inject(AfterSalesTabsetComponent, { host: true });

  @ViewChild('SupplierExceptionDialogTemplate')
  public supplierExceptionDialogTemplate: ElementRef;
  public supplierExceptionDialog: NgbModalRef;
  public supplierExceptionDialogForm: UntypedFormGroup;

  public acceptanceAllowed = false;
  public formValueChanged = false;
  public initialFormValue: any;
  public spinnerActive = false;
  public readonly validationMessages$ = this.translateService
    .get(['VALIDATION_INPUT_REQUIRED', 'SUPPLIER_NOT_FOUND_TITLE'])
    .pipe(
      map(translations => [
        {
          error: 'required',
          message: translations.VALIDATION_INPUT_REQUIRED,
        },
        {
          error: 'incorrectSupplierId',
          message: translations.SUPPLIER_NOT_FOUND_TITLE,
        },
      ]),
      shareReplay(),
    );

  public gridOptions: GridOptions;
  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public excelStyles: any;
  public multiSortKey: any;
  public gridApi: any;
  public frameworkComponents: any;
  public isCreateExcelSupplierExceptionsEnabled = true;
  public tooltipShowDelay = 0;
  private tmpParam: any;
  public supplierExceptionsOverviewData: any[] = [];

  private supplierExceptionsUpdate = false;

  public constructor() {
    this.frameworkComponents = {
      supplierExceptionsButtonCellRenderer: SupplierExceptionsButtonCellRenderer,
    };
  }

  public async ngOnInit(): Promise<void> {
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      context: {
        today: new Date().setHours(0, 0, 0, 0),
      },
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };
    this.gridOptions.rowClassRules = {
      'gray-characters':
        'data.exclusionTillDate?.length > 0 && new Date(data.exclusionTillDate).setHours(0, 0, 0, 0) < ctx.today',
    };

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      unSortIcon: true,
      floatingFilter: true,
    };

    this.translateService.get('AFTER_SALES_SUPPLIER_EXCEPTIONS_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('SUPPLIER_EXCEPTIONS_TABLE_SUPPLIER_ID'),
          field: 'supplierId',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('SUPPLIER_EXCEPTIONS_TABLE_SUPPLIER'),
          field: 'supplierName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('SUPPLIER_EXCEPTIONS_REASON'),
          field: 'exceptionReason',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('SUPPLIER_EXCEPTIONS_TABLE_EXCLUSION_FROM'),
          field: 'exclusionFromDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('SUPPLIER_EXCEPTIONS_TABLE_EXCLUSION_TILL'),
          field: 'exclusionTillDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          minWidth: 100,
          maxWidth: 100,
          filter: false,
          cellRenderer: 'supplierExceptionsButtonCellRenderer',
          cellRendererParams: {
            onDeleteClicked: this.onDeleteSupplierExceptioneBtn.bind(this),
            onEditClicked: this.onEditSupplierExceptionBtn.bind(this),
          },
          field: 'value',
          menuTabs: [],
          unSortIcon: false,
        },
      ];
    });

    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);
    this.multiSortKey = 'ctrl';
    this.initFormFields();
    this.initialFormValue = cloneDeep(this.supplierExceptionDialogForm.value);
    this.onChanges();
  }

  public onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.sizeColumnsToFit();
  }

  public onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }

  public enableSidebar(): void {
    if (this.afterSalesTabset.userHasAnyRight()) {
      this.sidebarHandleService.enableSidebarForView();
    }
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.tmpParam = event;
    this.gridApi = event.api;
    this.gridApi.setGridOption('headerHeight', 32);
    this.supplierExceptionsOverviewData = await lastValueFrom(
      this.supplierExceptionsService.getAllSupplierExceptions(),
    );
    event.api.setGridOption('rowData', this.supplierExceptionsOverviewData);
    this.isCreateExcelSupplierExceptionsEnabled = this.afterSalesTabset.userHasAnyRight();
  }

  public refreshTable(): void {
    this.onGridReady(this.tmpParam);
  }

  public onBtnCreateExcelSupplierExceptions(): void {
    try {
      this.gridApi.exportDataAsExcel(
        this.agGridExcelService.getExcelFrame(
          this.translateService.instant('AFTER_SALES_SUPPLIER_EXCEPTIONS_EXCEL_FILENAME'),
          this.translateService.instant('AFTER_SALES_SUPPLIER_EXCEPTIONS_TAB'),
          this.gridOptions,
        ),
      );
    } catch {
      const title: string = this.translateService.instant('EXCEL_EXPORT_ERROR_TITLE');
      const content: string = this.translateService.instant('EXCEL_EXPORT_ERROR_CONTENT');
      this.notificationServiceWrapper.addErrorNotification(title, content);
    }
  }

  private onChanges(): void {
    this.supplierExceptionDialogForm.valueChanges.subscribe(newFormValue => {
      this.formValueChanged = this.areFormFieldsChanged(this.initialFormValue, newFormValue);
    });
  }

  private areFormFieldsChanged(initialFormValue, newFormValue): boolean {
    let formChanged = false;
    Object.keys(initialFormValue).forEach((key: string) => {
      const initialFieldValue = initialFormValue[key];
      const newFieldValue = newFormValue[key];
      if (key === 'supplierNumber') {
        if (!this.areNumericValuesEqual(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      } else {
        if (!isEqual(initialFieldValue, newFieldValue) && !this.stringsNullOrEmpty(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      }
    });
    return formChanged;
  }

  private areNumericValuesEqual(initValue, newValue): boolean {
    return Number(initValue) === Number(newValue);
  }

  private stringsNullOrEmpty(initValue, newValue): boolean {
    return this.isStringNullOrEmpty(initValue) && this.isStringNullOrEmpty(newValue);
  }

  private isStringNullOrEmpty(inputString): boolean {
    return !inputString || inputString.length === 0;
  }

  public getContextMenuItems(): any {
    return [];
  }

  public ngOnDestroy(): void {
    this.sidebarHandleService.disableSidebarForView();
  }

  private initFormFields(): void {
    this.supplierExceptionDialogForm = new UntypedFormGroup({
      supplierId: new UntypedFormControl(
        { value: '', disabled: false },
        {
          validators: [Validators.required, Validators.pattern(/^\*$|^\d{6}$/)],
          asyncValidators: [this.validateSupplierId.bind(this)],
        },
      ),
      supplierName: new UntypedFormControl({ value: '', disabled: true }),
      exclusionFrom: new UntypedFormControl({ value: '', disabled: false }),
      exclusionTill: new UntypedFormControl({ value: '', disabled: false }),
      reason: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    });
    const exclusionFromDate: AbstractControl = this.supplierExceptionDialogForm.get('exclusionFrom');
    exclusionFromDate.setValidators([Validators.required, this.exclusionFromValidator()]);

    const exclusionTillDate: AbstractControl = this.supplierExceptionDialogForm.get('exclusionTill');
    exclusionTillDate.setValidators(this.exclusionTillValidatior());
  }

  public compareDate(date1: Date, date2: Date): number {
    // With Date object we can compare dates them using the >, <, <= or >=.
    // The ==, !=, ===, and !== operators require to use date.getTime(),
    // so we need to create a new instance of Date with 'new Date()'
    const d1 = formatDate(new Date(date1), 'yyyy-MM-dd', this.userService.userLanguage);
    const d2 = formatDate(new Date(date2), 'yyyy-MM-dd', this.userService.userLanguage);
    if (d1 > d2) return 1;
    if (d1 <= d2) return -1;
  }

  private exclusionTillValidatior(): ValidatorFn {
    return (): any => {
      const fromValue = this.supplierExceptionDialogForm.get('exclusionFrom').value;
      const tillValue = this.supplierExceptionDialogForm.get('exclusionTill').value;
      // checking null values
      if (!fromValue && !tillValue) {
        return null;
      } else if (!fromValue && tillValue) {
        return {
          dateNotBeforeToday: true,
        };
      } else if (fromValue && !tillValue) {
        return null;
      }
      // if values are set, comparing dates
      const dateFromGreaterThenDateTo = this.compareDate(fromValue, tillValue);
      const dateEqualAfterToday = moment(tillValue).isSameOrAfter(new Date(), 'day');
      return dateFromGreaterThenDateTo === 1 || !dateEqualAfterToday ? { dateNotBeforeToday: true } : null;
    };
  }

  // async validator: checks if given supplierId exists in db
  private validateSupplierId(control: AbstractControl): Observable<ValidationErrors | null> {
    return control.value?.toString()?.length > MIN_SUPPLIER_LENGTH
      ? this.supplierService.getSupplier({ supplierId: control.value }).pipe(
          map(supplierResponse => {
            if (supplierResponse?.name?.trim().length > 0) {
              this.supplierExceptionDialogForm.get('supplierName').enable();
              this.supplierExceptionDialogForm.get('supplierName').setValue(supplierResponse.name.trim());
              this.supplierExceptionDialogForm.get('supplierName').disable();
              return null;
            } else {
              this.supplierExceptionDialogForm.get('supplierName').setValue('');
              return { incorrectSupplierId: true };
            }
          }),
          catchError(() => of({ incorrectSupplierId: true })),
        )
      : null;
  }

  private exclusionFromValidator(): ValidatorFn {
    return (control: UntypedFormControl): any => {
      if (!control.value || !moment(control.value).isSameOrAfter(new Date(), 'day')) {
        return {
          dateNotBeforeToday: true,
        };
      } else if (control.value) {
        return null;
      } else {
        return {
          dateNotFilled: true,
        };
      }
    };
  }

  public onNewSupplierExceptionBtn(): void {
    this.supplierExceptionsUpdate = false;
    this.openSupplierExceptionDialog();
  }

  public userHasSuppliersAndRatesRight(): boolean {
    return checkUserRight(this.userService.getRights(), UserRights.AFTERSALES_SUPPLIER_AND_RATES);
  }

  private onEditSupplierExceptionBtn(event: any): void {
    this.supplierExceptionsUpdate = true;
    this.openSupplierExceptionDialog(event.rowData);
  }

  private onDeleteSupplierExceptioneBtn(event: any): void {
    const supplierId = event.rowData.supplierId;
    const exclusionFromDate = event.rowData.exclusionFromDate;

    this.translateService
      .get([
        'SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_DELETE',
        'SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_DELETE_MESSAGE',
        'OK',
        'CANCEL',
      ])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_DELETE;
        const message: string = this.translateService.instant(
          'SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_DELETE_MESSAGE',
          { 0: supplierId },
        );
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
          () => {
            this.spinnerActive = true;
            this.supplierExceptionsService.deleteSupplierException(supplierId, exclusionFromDate).subscribe({
              next: () => {
                const title2: string = this.translateService.instant('SUPPLIER_EXCEPTIONS_DIALOG_DELETED_TITLE');
                const content2: string = this.translateService.instant('SUPPLIER_EXCEPTIONS_DIALOG_DELETED_MESSAGE', {
                  0: supplierId,
                });
                this.notificationServiceWrapper.addSuccessNotification(title2, content2);
                this.refreshTable();
                this.spinnerActive = false;
              },
              error: () => {
                const title3: string = this.translateService.instant('SUPPLIER_EXCEPTIONS_DIALOG_DELETED_ERROR_TITLE');
                const content3: string = this.translateService.instant(
                  'SUPPLIER_EXCEPTIONS_DIALOG_DELETED_ERROR_MESSAGE',
                  { 0: supplierId },
                );
                this.notificationServiceWrapper.addErrorNotification(title3, content3);
                this.spinnerActive = false;
              },
            });
          },
          () => {},
        );
      });
  }

  public openSupplierExceptionDialog(rowData?: any): void {
    this.supplierExceptionDialog = this.modalService.open(this.supplierExceptionDialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });

    if (rowData) {
      this.supplierExceptionDialogForm.patchValue({
        supplierId: rowData.supplierId,
        supplierName: rowData.supplierName,
        exclusionFrom: rowData.exclusionFromDate,
        exclusionTill: rowData.exclusionTillDate,
        reason: rowData.exceptionReason,
      });

      this.supplierExceptionDialogForm.get('supplierId').disable();
      this.supplierExceptionDialogForm.get('exclusionFrom').disable();

      this.initialFormValue = cloneDeep(this.supplierExceptionDialogForm.value);
    } else {
      this.supplierExceptionDialogForm.get('supplierId').enable();
      this.supplierExceptionDialogForm.get('exclusionFrom').enable();
    }
  }

  public onAcceptSupplierExceptionDialog(): void {
    if (this.supplierExceptionsUpdate) {
      this.translateService
        .get([
          'SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG',
          'SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_MESSAGE',
          'OK',
          'CANCEL',
        ])
        .subscribe((translate: { [key: string]: any }) => {
          const title: string = translate.SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG;
          const message: string = translate.SUPPLIER_EXCEPTIONS_CONFIRMATION_DIALOG_MESSAGE;
          const ok: string = translate.OK;
          const dismiss: string = translate.CANCEL;

          this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(() => {
            this.saveSupplierException();
          });
        });
    } else {
      this.saveSupplierException();
    }
  }

  private saveSupplierException(): void {
    this.spinnerActive = true;
    this.disableSupplierExceptionsForm(true);

    this.supplierExceptionsService
      .saveSupplierException(this.createModelOfSupplierException(), this.supplierExceptionsUpdate)
      .subscribe({
        next: result => {
          const title: string = this.translateService.instant('SAVED_TITLE');
          const content: string = this.translateService.instant('SUPPLIER_EXCEPTIONS_DIALOG_SAVED', {
            0: result.supplierId,
          });
          this.notificationServiceWrapper.addSuccessNotification(title, content);
          this.refreshTable();
        },
        error: err => {
          const title: string = this.translateService.instant('NOTIFICATION_SAVE_ERROR_HEADER');
          const content =
            err.error.errorCode === 14_000
              ? this.translateService.instant('SUPPLIER_EXCEPTIONS_DIALOG_SAVED_ERROR_MESSAGE')
              : this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER');
          this.notificationServiceWrapper.addErrorNotification(title, content);
          this.disableSupplierExceptionsForm(false);
          this.spinnerActive = false;
          this.onCloseSupplierExceptionDialog();
        },
        complete: () => {
          this.disableSupplierExceptionsForm(false);
          this.spinnerActive = false;
          this.onCloseSupplierExceptionDialog();
        },
      });
  }

  public onCloseSupplierExceptionDialog(): void {
    this.resetSupplierExceptionForm();
    this.supplierExceptionDialog.close();
  }

  private createModelOfSupplierException(): SupplierExceptionModel {
    const supplierExceptionDialogData = this.supplierExceptionDialogForm.value;

    const supplierExceptionModel = new SupplierExceptionModel();
    // set data to model
    supplierExceptionModel.supplierId = supplierExceptionDialogData.supplierId;
    supplierExceptionModel.supplierName = supplierExceptionDialogData.supplierName;
    supplierExceptionModel.exclusionFromDate = supplierExceptionDialogData.exclusionFrom;
    supplierExceptionModel.exclusionTillDate = supplierExceptionDialogData.exclusionTill;
    supplierExceptionModel.exceptionReason = supplierExceptionDialogData.reason;
    return supplierExceptionModel;
  }

  private resetSupplierExceptionForm(): void {
    this.supplierExceptionDialogForm.setValue({
      supplierId: '',
      supplierName: '',
      exclusionFrom: '',
      exclusionTill: '',
      reason: '',
    });

    this.supplierExceptionDialogForm.get('supplierName').disable();
  }

  public isAcceptanceAllowed(): boolean {
    return (
      this.supplierExceptionDialogForm &&
      this.supplierExceptionDialogForm.dirty &&
      this.supplierExceptionDialogForm.valid &&
      this.formValueChanged
    );
  }

  public disableSupplierExceptionsForm(disableFlag: boolean): any {
    return disableFlag === true
      ? this.supplierExceptionDialogForm.disable()
      : this.supplierExceptionDialogForm.enable();
  }
}
